import { Box, Button, Icon, Text, TextButton } from '@zillow/constellation-10';
import {
  IconCheckmarkCircleFilled,
  IconMailFilled,
  IconPhoneCircleFilled,
  IconWarningFilled,
} from '@zillow/constellation-icons';

import numberUtils from '../../../../srcLegacy/shared/utils/numberUtils';

export type ContactMethodDetailProps = {
  data: { value: string; isVerified: boolean; canBeRemoved: boolean };
  emailVerificationText?: string;
  inManageContactModal: boolean;
  onDelete?: ({ value }: { value: string }) => void;
  onVerifyPhoneClick?: (value: string) => void;
  onVerifyEmailClick?: (value: string) => void;
};

const ContactMethodDetail = ({
  data: { isVerified, value, canBeRemoved },
  emailVerificationText = 'Send verification email',
  inManageContactModal = false,
  onDelete,
  onVerifyPhoneClick,
  onVerifyEmailClick,
}: ContactMethodDetailProps) => {
  const isEmail = isNaN(parseInt(value, 10));
  const statusText = isVerified ? (
    <>
      <Icon
        render={<IconCheckmarkCircleFilled />}
        size="sm"
        css={{ color: 'icon.onImpact.feedback.success.hero', marginRight: '400' }}
      />{' '}
      Verified
    </>
  ) : (
    <>
      <Icon
        render={<IconWarningFilled />}
        size="sm"
        css={{ color: 'text.feedback.warning.hero', marginRight: '400' }}
      />{' '}
      Not verified
    </>
  );

  const getHelpText = () => inManageContactModal && isVerified && 'A verified contact cannot be removed';

  const getCTAButton = () => {
    if (inManageContactModal) {
      return canBeRemoved ? <TextButton onClick={() => onDelete?.({ value })}>Remove</TextButton> : null;
    }
    return (
      <Button
        tone="brand"
        emphasis="filled"
        onClick={() => (isEmail ? onVerifyEmailClick?.(value) : onVerifyPhoneClick?.(value))}
      >
        {isEmail ? emailVerificationText : 'Verify now'}
      </Button>
    );
  };

  return (
    <Box
      data-testid="ContactMethodDetail"
      css={{
        borderTop: '1px solid {colors.border.subtle}',
        padding: '400',
        '&:last-child': { borderBottom: '1px solid {colors.border.subtle}' },
      }}
    >
      <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)' }}>
        <Box css={{ gridColumnStart: '2', gridColumnEnd: 'span 1' }}>
          <Box css={{ width: '30px', marginRight: '400' }}>
            <Icon
              render={isEmail ? <IconMailFilled /> : <IconPhoneCircleFilled />}
              size="lg"
              css={{ color: 'icon.accent.blue.strong' }}
            />
          </Box>
        </Box>
        <Box css={{ gridColumnStart: '3', gridColumnEnd: 'span 6' }}>
          <Box css={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <Text css={{ wordWrap: 'break-word' }} className="fs-mask" asChild>
              <p>
                <b>{isEmail ? value : numberUtils.formatPhoneNumberInput(value)}</b>
              </p>
            </Text>
            <Text textStyle="body-sm" color="text.subtle" css={{ display: 'block' }} asChild>
              <p>{getHelpText()}</p>
            </Text>
            <Text
              textStyle="body-sm"
              color="text.subtle"
              css={{ display: 'flex', alignItems: 'center', fontWeight: 600, lg: { display: 'none' } }}
            >
              {statusText}
            </Text>
            {!isVerified && <Box css={{ marginTop: '200' }}>{getCTAButton()}</Box>}
          </Box>
        </Box>
        <Box css={{ gridColumnStart: '10', gridColumnEnd: 'span 2' }}>
          <Text
            textStyle="body-sm"
            color="text.subtle"
            css={{ display: 'none', lg: { display: 'flex', alignItems: 'center', fontWeight: 600 } }}
          >
            {statusText}
          </Text>
        </Box>
      </Box>
    </Box>
  );
};

export default ContactMethodDetail;
